import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";
import { SectionLabel } from "@/components/SectionLabel";
import { AccentHeading } from "@/components/AccentHeading";

const variants = {
  enter: (d) => ({ opacity: 0, x: d > 0 ? 50 : -50 }),
  center: { opacity: 1, x: 0 },
  exit: (d) => ({ opacity: 0, x: d > 0 ? -50 : 50 }),
};

// Card content shared by the visible (animated) card and the invisible
// "sizer" cards, so every slide is measured and rendered identically.
const CardBody = ({ item }) => (
  <>
    <span className="self-start rounded-full border border-[var(--border-subtle)] bg-[rgba(12,12,12,0.4)] px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] text-[var(--accent-cream)]">
      {item.project}
    </span>
    <p className="mt-6 flex-1 font-playfair text-lg italic leading-relaxed text-[var(--text-primary)] sm:text-xl md:text-2xl">
      &ldquo;{item.quote}&rdquo;
    </p>
    <div className="mt-7 flex items-center gap-4">
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[image:var(--gradient-cta)] font-inter font-semibold text-[#0C0C0C]">
        {item.initials}
      </span>
      <div>
        <div className="font-inter font-semibold text-[var(--accent-secondary)]">{item.author}</div>
        <div className="text-sm text-[var(--text-muted)]">Homeowner, {item.project}</div>
      </div>
    </div>
  </>
);

const cardClasses =
  "glass-card relative flex h-full flex-col rounded-[28px] p-6 sm:p-8 md:p-10 [grid-area:1/1]";

export const Testimonials = () => {
  const [[index, dir], setState] = useState([0, 0]);
  const [paused, setPaused] = useState(false);
  const count = TESTIMONIALS.length;

  const paginate = (d) => setState(([i]) => [(i + d + count) % count, d]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => paginate(1), 5600);
    return () => clearInterval(t);
    // paginate is stable (depends only on setState/count); re-run on slide change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, index]);

  const item = TESTIMONIALS[index];

  return (
    <section
      data-testid="testimonials"
      className="bg-[var(--bg-warm)] py-16 md:py-24 lg:py-28"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container-pad grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
        <div>
          <SectionLabel>TESTIMONIALS</SectionLabel>
          <AccentHeading as="h2" text="Our Clients Speak *Boldly.*" className="mt-4 block text-4xl leading-[1.03] md:text-5xl lg:text-6xl" />
          <p className="mt-5 max-w-[40ch] text-[var(--text-secondary)]">Real stories from families who found their home with Godavari Builders.</p>
          <div className="mt-8 flex items-center gap-4">
            <button data-testid="testimonials-prev-button" aria-label="Previous testimonial" onClick={() => paginate(-1)} className="h-11 w-11 grid place-items-center rounded-full border border-[var(--border-subtle)] text-[var(--text-primary)] transition-colors hover:border-[var(--border-hover)] hover:bg-[rgba(255,245,230,0.05)] focus-ring">
              <ChevronLeft size={18} />
            </button>
            <button data-testid="testimonials-next-button" aria-label="Next testimonial" onClick={() => paginate(1)} className="h-11 w-11 grid place-items-center rounded-full border border-[var(--border-subtle)] text-[var(--text-primary)] transition-colors hover:border-[var(--border-hover)] hover:bg-[rgba(255,245,230,0.05)] focus-ring">
              <ChevronRight size={18} />
            </button>
            <span className="ml-1 font-inter text-sm text-[var(--text-muted)]">
              <span className="text-[var(--accent-secondary)]">{String(index + 1).padStart(2, "0")}</span> / {String(count).padStart(2, "0")}
            </span>
          </div>
        </div>

        <div data-testid="testimonials-carousel" className="relative">
          <Quote size={120} className="pointer-events-none absolute -top-8 -left-3 z-0 text-[var(--accent-primary)] opacity-[0.08]" />

          {/* Single grid cell: invisible sizers reserve the tallest height;
              the active animated card overlays them in the same cell. */}
          <div className="relative z-10 grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={`sizer-${i}`} aria-hidden="true" className={`${cardClasses} invisible`}>
                <CardBody item={t} />
              </div>
            ))}

            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={index}
                custom={dir}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, info) => {
                  if (info.offset.x < -60) paginate(1);
                  else if (info.offset.x > 60) paginate(-1);
                }}
                className={`${cardClasses} cursor-grab active:cursor-grabbing`}
              >
                <CardBody item={item} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
